import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAltRounded from "@mui/icons-material/PersonAddAltRounded";
import { FUNCTIONS, ADMIN_TOOLS } from "shared/accessPermissions";
import { getAccessUsers, saveAccessUser } from "services/userAccess";
const empty = () => ({ email: "", name: "", active: true, functions: [], adminTools: [] });
export default function UsersAdmin() {
  const [users, setUsers] = useState([]),
    [owners, setOwners] = useState([]);
  const [form, setForm] = useState(empty),
    [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false);
  const [error, setError] = useState(""),
    [success, setSuccess] = useState("");
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getAccessUsers();
      setUsers(result.users);
      setOwners(result.owners);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const toggle = (field, key) =>
    setForm((current) => {
      const values = current[field].includes(key)
        ? current[field].filter((value) => value !== key)
        : [...current[field], key];
      return {
        ...current,
        [field]: values,
        ...(field === "functions" && !values.includes("admin") ? { adminTools: [] } : {}),
      };
    });
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const saved = await saveAccessUser(form);
      setUsers((current) =>
        [...current.filter((user) => user.email !== saved.email), saved].sort((a, b) =>
          a.name.localeCompare(b.name)
        )
      );
      setSuccess(`Permissões de ${saved.name} salvas.`);
      setForm(empty());
      setEditing(false);
      window.dispatchEvent(new CustomEvent("pso:access-updated"));
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };
  const valid =
    form.name.trim() &&
    form.email.trim() &&
    (!form.active || form.functions.length > 0) &&
    (!form.functions.includes("admin") || form.adminTools.length > 0);
  return (
    <Box className="access-workspace">
      <Typography className="files-eyebrow">CONTROLE DE ACESSO</Typography>
      <Typography variant="h5" mb={1}>
        Usuários e permissões
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Defina quais funções cada pessoa pode acessar e quais ferramentas administrativas ficam
        disponíveis.
      </Typography>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={!busy && !loading ? <Button onClick={load}>Recarregar lista</Button> : undefined}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}
      <Box className="access-layout">
        <Box component="form" onSubmit={save} className="access-editor">
          <Typography variant="h6" mb={0.5}>
            {editing ? "Editar acesso" : "Adicionar usuário"}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Use o mesmo e-mail com que a pessoa entra na plataforma. O cadastro aqui não envia
            convite nem cria uma conta de login.
          </Typography>
          <Stack spacing={2.5}>
            <TextField
              label="Nome"
              required
              fullWidth
              value={form.name}
              disabled={busy}
              inputProps={{ maxLength: 160 }}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="E-mail de acesso"
              type="email"
              required
              fullWidth
              value={form.email}
              disabled={busy || editing}
              inputProps={{ maxLength: 254 }}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              helperText="As permissões exigem que este e-mail esteja verificado na conta."
            />
          </Stack>
          <FormControlLabel
            sx={{ my: 2 }}
            control={
              <Switch
                color="success"
                checked={form.active}
                disabled={busy}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
            }
            label="Acesso ativo"
          />
          {!form.active && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este usuário ficará sem acesso às funções da plataforma.
            </Alert>
          )}
          <Box component="fieldset" className="access-fieldset" disabled={busy}>
            <Typography component="legend" variant="subtitle2">
              Funções visíveis no menu
            </Typography>
            <Box className="access-options">
              {Object.entries(FUNCTIONS).map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      color="success"
                      checked={form.functions.includes(key)}
                      onChange={() => toggle("functions", key)}
                    />
                  }
                  label={label}
                />
              ))}
            </Box>
          </Box>
          {form.functions.includes("admin") && (
            <Box component="fieldset" className="access-fieldset access-tools" disabled={busy}>
              <Typography component="legend" variant="subtitle2">
                Ferramentas da Administração
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Selecione as ferramentas que esta pessoa poderá utilizar.
              </Typography>
              <Box className="access-options">
                {Object.entries(ADMIN_TOOLS).map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        color="success"
                        checked={form.adminTools.includes(key)}
                        onChange={() => toggle("adminTools", key)}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
              {form.adminTools.includes("users") && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  “Usuários e permissões” permite gerenciar acessos de outras pessoas, inclusive
                  conceder ferramentas administrativas.
                </Alert>
              )}
              {!form.adminTools.length && (
                <Typography variant="caption" color="error">
                  Escolha ao menos uma ferramenta.
                </Typography>
              )}
            </Box>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} mt={3}>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={busy || !valid}
              startIcon={
                busy ? <CircularProgress size={18} color="inherit" /> : <PersonAddAltRounded />
              }
            >
              {busy ? "Salvando…" : editing ? "Salvar alterações" : "Adicionar usuário"}
            </Button>
            {editing && (
              <Button
                disabled={busy}
                onClick={() => {
                  setForm(empty());
                  setEditing(false);
                }}
              >
                Cancelar edição
              </Button>
            )}
          </Stack>
        </Box>
        <Box className="access-directory">
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Acessos cadastrados</Typography>
            <Button onClick={load} disabled={loading || busy}>
              Atualizar
            </Button>
          </Stack>
          {loading ? (
            <CircularProgress aria-label="Carregando usuários" size={26} />
          ) : (
            <>
              {!users.length && (
                <Box className="access-empty">
                  <Typography variant="body2" color="text.secondary">
                    Nenhum acesso personalizado. Adicione uma pessoa ao lado para definir suas
                    permissões.
                  </Typography>
                </Box>
              )}
              {users.map((user) => (
                <Box className="access-person" key={user.email}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">{user.name}</Typography>
                      <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
                        {user.email}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={user.active ? "Ativo" : "Desativado"}
                      color={user.active ? "success" : "default"}
                    />
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={0.75} my={1.5}>
                    {user.functions.map((key) => (
                      <Chip
                        size="small"
                        variant="outlined"
                        key={key}
                        label={FUNCTIONS[key] || key}
                      />
                    ))}
                  </Stack>
                  {user.functions.includes("admin") && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Administração:{" "}
                      {user.adminTools.map((key) => ADMIN_TOOLS[key] || key).join(", ")}
                    </Typography>
                  )}
                  <Button
                    sx={{ mt: 1 }}
                    disabled={busy}
                    onClick={() => {
                      setForm({
                        email: user.email,
                        name: user.name,
                        active: user.active,
                        functions: [...user.functions],
                        adminTools: [...user.adminTools],
                      });
                      setEditing(true);
                      setSuccess("");
                    }}
                  >
                    Editar acesso de {user.name}
                  </Button>
                </Box>
              ))}
              <Box className="access-owners">
                <Typography variant="subtitle2" mb={1}>
                  Administradores principais
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Mantêm acesso completo e não podem ser alterados nesta tela.
                </Typography>
                {owners.map((email) => (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ overflowWrap: "anywhere" }}
                    key={email}
                  >
                    {email}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
